import * as React from 'react';

import { TextField, TextFieldProps } from '@mui/material';
import { FieldValues, UseControllerProps } from 'react-hook-form';

import _startCase from 'lodash/startCase';

import { useFormUpdateProxy } from '../core/context/FormUpdateProxyContext';
import useEditableController from '../core/api/hooks/common/useEditableController';

import ReadOnlyField from './ReadOnlyField';
import EditableField from './EditableField';

interface IControlledTextField<TFieldValues extends FieldValues> extends UseControllerProps<TFieldValues> {
  label?: string;
  type?: string;
  customValue?: string | number | boolean;
  readOnly?: boolean;
  disableEditPopper?: boolean,
  TextFieldProps?: TextFieldProps;
  transform?: (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => string;
}

function ControlledTextField<TFieldValues extends FieldValues>({
  name,
  label,
  control,
  readOnly,
  disableEditPopper,
  type = 'text',
  TextFieldProps: textFieldProps,
  rules,
  transform,
}: IControlledTextField<TFieldValues>): JSX.Element {
  const { requestUpdate } = useFormUpdateProxy();
  const {
    field,
    fieldState,
    revertChanges,
    commitChanges,
  } = useEditableController<TFieldValues>({
    name,
    control,
    defaultValue: '' as any,
  });
  return (
    <EditableField
      fieldName={name}
      renderMode={readOnly ? 'read' : 'edit'}
      disableEditPopper={disableEditPopper}
      saveSuccess={() => {
        commitChanges();
        requestUpdate();
      }}
      onEditCancel={() => revertChanges()}
      renderEditField={() => (
        <TextField
          {...textFieldProps}
          fullWidth
          variant="outlined"
          label={`${label || textFieldProps?.label || _startCase(name)}`}
          error={fieldState.invalid}
          helperText={fieldState.invalid && fieldState.error ? fieldState.error.message : null}
          required={rules?.required === true || false}
          type={type}
          aria-labelledby={`field-label=${name}`}
          onChange={(e) => field.onChange(transform ? transform(e) : e)}
          onBlur={field.onBlur}
          value={field.value || ''}
        />
      )}
      renderReadField={() => (
        <ReadOnlyField
          name={label || _startCase(name)}
          value={field.value || ''}
        />
      )}
    />
  );
}

export default ControlledTextField;
